import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type TouchEvent as ReactTouchEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";
import {
  Group as KonvaGroup,
  Layer,
  Line as KonvaLine,
  Rect as KonvaRect,
  Stage,
  Transformer,
} from "react-konva";
import type { Group as KonvaGroupNode } from "konva/lib/Group";
import type { Transformer as KonvaTransformerNode } from "konva/lib/shapes/Transformer";
import { Brush, Hand, Loader2, MousePointer2, Redo2, Undo2, ZoomIn, ZoomOut } from "lucide-react";
import {
  DEFAULT_SOCIAL_POST_IMAGE_REGENERATION_BRUSH_RADIUS,
  type SocialPostSceneLeafBlockV1,
  type SocialPostImageRegenerationTaskRequestV1,
  type SocialPostScenePlanV1,
} from "@runcheetah/shared";
import { SocialPostSceneBlockContent } from "@runcheetah/ui";

import {
  appendImageBrushPoint,
  canSubmitImageBrushFeedback,
  resolveBlockInteractionState,
  resolveImageBrushLocalPoint,
  resolveImageBrushToolAvailability,
  resolveMarqueeRect,
  resolveMarqueeSelectedBlockIds,
  resolveBlockDragPosition,
  resolveBlockTransformFrame,
  resolveCommittedTextEdit,
  resolvePannedWorkspaceTransform,
  resolveTextEditOverlayLayout,
  resolveTextEditKeyAction,
  resolveTextEditorOverlayFrame,
  resolveTransformerBoundBox,
  resolveWorkspaceFitTransform,
  resolveWorkspacePointerFromClientPoint,
  resolveWorkspaceWheelZoomFactor,
  resolveWorkspaceZoomTransform,
  shouldUseBlockBoundsHitArea,
  type ImageBrushStroke,
  type MarqueeRect,
  type WorkspaceTransform,
} from "./campaign-creative-scene-preview-utils.ts";
import { InstagramPostChrome, InstagramPostMetadataEditor } from "./instagram-post-chrome.tsx";
import {
  resolveSocialPostChromeFrame,
  resolveSocialPostChromeVariant,
} from "@/lib/social-post-chrome";

export type CampaignCreativeLinkedAsset = {
  generatedAssetId: string;
  blockId: string;
  signedUrl: string;
  width: number | null;
  height: number | null;
};

export type CampaignCreativeBrandkit = {
  logo: {
    objectKey: string;
    signedUrl: string;
  } | null;
  fonts: Array<{
    family: string;
    source: "brand_confirmed" | "google_font";
    selectedVariant: string;
    loadUrl: string;
    assetObjectKey: string | null;
  }>;
};

export type CampaignCreativeLogoAsset = {
  objectKey: string;
  signedUrl: string;
  isPrimary?: boolean;
};

type RenderContext = {
  scale: number;
  canvas: SocialPostScenePlanV1["scene"]["canvas"];
  blockById: Map<string, SceneBlock>;
  imageAssetByBlockId: Map<string, CampaignCreativeLinkedAsset>;
  logoAssets: CampaignCreativeLogoAsset[];
  brandkit: CampaignCreativeBrandkit;
  selectedBlockId?: string | null;
  selectedBlockIds?: string[];
  selectedTransformBlockId?: string | null;
  selectedTransformBlockIds?: string[];
  editingTextBlockId?: string | null;
  editingTextValue?: string | null;
  editingTextSelection?: TextEditSelection | null;
  showTextEditCaret?: boolean;
  isPanToolActive?: boolean;
  isImageBrushToolActive?: boolean;
  onSelectBlock?: (blockId: string, options?: SelectBlockOptions) => void;
  onUpdateBlock?: (blockId: string, changes: Record<string, unknown>) => void;
  onUpdateBlocks?: (updates: Array<{ blockId: string; changes: Record<string, unknown> }>) => void;
  onStartTextEdit?: (blockId: string) => void;
  registeredNodeById?: Map<string, KonvaGroupNode>;
  registerNode?: (blockId: string, node: KonvaGroupNode | null) => void;
};

type SceneBlock = SocialPostSceneLeafBlockV1;
type SceneTextBlock = Extract<SceneBlock, { type: "text" }>;
type SceneImageBrushBlock = Extract<SceneBlock, { type: "image" }>;
type EditingTextState = { blockId: string; value: string; previousText: string };
type TextEditSelection = { start: number; end: number };
type WorkspaceTool = "edit" | "pan" | "brush";
type SelectBlockOptions = { toggle?: boolean };
type ImageRegenerationSubmitInput = Pick<
  Extract<SocialPostImageRegenerationTaskRequestV1, { mode: "partial" }>,
  "blockId" | "sourceGeneratedAssetId" | "prompt" | "brushRadius" | "strokes"
>;
const EMPTY_ACTIVE_IMAGE_REGENERATION_BLOCK_IDS: readonly string[] = [];
type WorkspacePointerEvent = {
  target: {
    getStage: () => { getPointerPosition: () => { x: number; y: number } | null } | null;
  };
};
const TEXT_EDIT_SYNC_DELAY_MS = 250;
const TEXT_EDIT_CARET_BLINK_MS = 530;

const isSceneImageBrushBlock = (block: SceneBlock): block is SceneImageBrushBlock =>
  block.type === "image";

function getWorkspacePointer(event: WorkspacePointerEvent) {
  return event.target.getStage()?.getPointerPosition() ?? null;
}

export function CampaignCreativeScenePreview({
  plan,
  linkedAssets,
  brandkit,
  logoAssets = [],
  profileLabel,
  caption = "",
  hashtags = [],
  maxWidth,
  selectedBlockId,
  selectedBlockIds,
  selectedTransformBlockId,
  selectedTransformBlockIds,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  onSelectBlock,
  onSelectBlocks,
  onUpdateBlock,
  onUpdateBlocks,
  onCaptionChange,
  onHashtagsChange,
  onCaptionDoubleClick,
  onSubmitImageRegeneration,
  isImageRegenerationPending = false,
  activeImageRegenerationBlockIds,
  imageRegenerationError = null,
  showWorkspaceControls = true,
  workspaceControlsPortal = null,
  workspaceZoomLabel,
  onWorkspaceZoomOut,
  onWorkspaceFitView,
  onWorkspaceZoomIn,
  onWorkspaceWheelZoom,
  flat = false,
  artboard = false,
}: {
  plan: SocialPostScenePlanV1;
  linkedAssets: CampaignCreativeLinkedAsset[];
  brandkit: CampaignCreativeBrandkit | null;
  logoAssets?: CampaignCreativeLogoAsset[];
  profileLabel?: string;
  caption?: string;
  hashtags?: string[];
  maxWidth?: number;
  selectedBlockId?: string | null;
  selectedBlockIds?: string[];
  selectedTransformBlockId?: string | null;
  selectedTransformBlockIds?: string[];
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onSelectBlock?: (blockId: string, options?: SelectBlockOptions) => void;
  onSelectBlocks?: (blockIds: string[]) => void;
  onUpdateBlock?: (blockId: string, changes: Record<string, unknown>) => void;
  onUpdateBlocks?: (updates: Array<{ blockId: string; changes: Record<string, unknown> }>) => void;
  onCaptionChange?: (caption: string) => void;
  onHashtagsChange?: (hashtags: string[]) => void;
  onCaptionDoubleClick?: () => void;
  onSubmitImageRegeneration?: (request: ImageRegenerationSubmitInput) => Promise<boolean> | boolean;
  isImageRegenerationPending?: boolean;
  activeImageRegenerationBlockIds?: readonly string[];
  imageRegenerationError?: string | null;
  showWorkspaceControls?: boolean;
  workspaceControlsPortal?: HTMLElement | null;
  workspaceZoomLabel?: string;
  onWorkspaceZoomOut?: () => void;
  onWorkspaceFitView?: () => void;
  onWorkspaceZoomIn?: () => void;
  onWorkspaceWheelZoom?: (factor: number) => void;
  flat?: boolean;
  artboard?: boolean;
}) {
  const intl = useIntl();
  useSceneBrandkitFonts(brandkit);
  const resolvedProfileLabel =
    profileLabel ??
    intl.formatMessage({
      id: "campaignCreativeScenePreview.defaultProfileLabel",
      defaultMessage: "brand",
    });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const workspaceSurfaceRef = useRef<HTMLDivElement | null>(null);
  const textEditorRef = useRef<HTMLTextAreaElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [editingText, setEditingText] = useState<EditingTextState | null>(null);
  const [editingTextSelection, setEditingTextSelection] = useState<TextEditSelection | null>(null);
  const [showTextEditCaret, setShowTextEditCaret] = useState(true);
  const [workspaceTool, setWorkspaceTool] = useState<WorkspaceTool>("edit");
  const [workspaceTransform, setWorkspaceTransform] = useState<WorkspaceTransform | null>(null);
  const [panDrag, setPanDrag] = useState<{
    pointer: { x: number; y: number };
    transform: WorkspaceTransform;
  } | null>(null);
  const [marqueeDrag, setMarqueeDrag] = useState<{
    anchor: { x: number; y: number };
    rect: MarqueeRect;
  } | null>(null);
  const [imageBrushPrompt, setImageBrushPrompt] = useState("");
  const [imageBrushStrokes, setImageBrushStrokes] = useState<ImageBrushStroke[]>([]);
  const [activeImageBrushStrokeId, setActiveImageBrushStrokeId] = useState<string | null>(null);
  const transformerRef = useRef<KonvaTransformerNode | null>(null);
  const nodeMapRef = useRef(new Map<string, KonvaGroupNode>());
  const lastWorkspacePointerRef = useRef<{ x: number; y: number } | null>(null);
  const imageBrushStrokeCounterRef = useRef(0);

  useEffect(() => {
    const element = containerRef.current;

    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateSize = (nextWidth: number, nextHeight: number) => {
      const width = Math.max(0, Math.floor(nextWidth));
      const height = Math.max(0, Math.floor(nextHeight));
      setContainerWidth((current) => (current === width ? current : width));
      setContainerHeight((current) => (current === height ? current : height));
    };

    updateSize(element.clientWidth, element.clientHeight);

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      updateSize(rect?.width ?? element.clientWidth, rect?.height ?? element.clientHeight);
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const sortedBlocks = useMemo(
    () => plan.scene.blocks.toSorted((a, b) => a.zIndex - b.zIndex) as SceneBlock[],
    [plan.scene.blocks],
  );
  const blockById = useMemo(
    () => new Map(sortedBlocks.map((block) => [block.id, block])),
    [sortedBlocks],
  );
  const resolvedSelectedBlockIds = useMemo(
    () => selectedBlockIds ?? (selectedBlockId ? [selectedBlockId] : []),
    [selectedBlockId, selectedBlockIds],
  );
  const resolvedSelectedTransformBlockIds = useMemo(
    () => selectedTransformBlockIds ?? (selectedTransformBlockId ? [selectedTransformBlockId] : []),
    [selectedTransformBlockId, selectedTransformBlockIds],
  );
  const imageAssetByBlockId = useMemo(
    () => new Map(linkedAssets.map((asset) => [asset.blockId, asset] as const)),
    [linkedAssets],
  );
  const defaultImageBrushBlock = useMemo<SceneImageBrushBlock | null>(() => {
    let firstImageBlock: SceneImageBrushBlock | null = null;

    for (const block of sortedBlocks) {
      if (!isSceneImageBrushBlock(block)) {
        continue;
      }

      firstImageBlock ??= block;
      if (imageAssetByBlockId.has(block.id)) {
        return block;
      }
    }

    return firstImageBlock;
  }, [imageAssetByBlockId, sortedBlocks]);
  const measuredWidth =
    containerWidth > 0 ? Math.max(containerWidth, 320) : plan.scene.canvas.width;
  const availableWidth = maxWidth ? Math.min(measuredWidth, maxWidth) : measuredWidth;
  const chromeVariant = resolveSocialPostChromeVariant(plan.platform, plan.postType);
  const chromeFrame = resolveSocialPostChromeFrame({
    variant: chromeVariant,
    mediaWidth: plan.scene.canvas.width,
    mediaHeight: plan.scene.canvas.height,
  });
  const artboardPadding = 18;
  const artboardZoom = Math.max(0.1, (availableWidth - artboardPadding * 2) / chromeFrame.width);
  const workspaceHeight = artboard
    ? Math.ceil(chromeFrame.height * artboardZoom + artboardPadding * 2)
    : containerHeight > 0
      ? containerHeight
      : 720;
  const fitTransform = useMemo(
    () =>
      resolveWorkspaceFitTransform({
        viewportWidth: availableWidth,
        viewportHeight: workspaceHeight,
        stageWidth: chromeFrame.width,
        stageHeight: chromeFrame.height,
        padding: artboard ? artboardPadding : 88,
      }),
    [artboard, availableWidth, chromeFrame.height, chromeFrame.width, workspaceHeight],
  );
  const activeWorkspaceTransform = workspaceTransform ?? fitTransform;
  const canvasOffsetX =
    activeWorkspaceTransform.x + chromeFrame.mediaX * activeWorkspaceTransform.zoom;
  const canvasOffsetY =
    activeWorkspaceTransform.y + chromeFrame.mediaY * activeWorkspaceTransform.zoom;
  const viewport = {
    width: Math.ceil(availableWidth),
    height: workspaceHeight,
    scale: activeWorkspaceTransform.zoom,
    previewWidth: Math.round(plan.scene.canvas.width * activeWorkspaceTransform.zoom),
    previewHeight: Math.round(plan.scene.canvas.height * activeWorkspaceTransform.zoom),
    canvasOffsetX,
    canvasOffsetY,
  };
  const workspaceCenter = { x: viewport.width / 2, y: viewport.height / 2 };
  const scale = viewport.scale;
  const previewWidth = viewport.previewWidth;
  const previewHeight = viewport.previewHeight;
  const selectedTransformBlockSnapshot = useMemo(() => {
    if (resolvedSelectedTransformBlockIds.length !== 1) {
      return null;
    }

    return findSceneBlockById(sortedBlocks, resolvedSelectedTransformBlockIds[0] ?? "");
  }, [resolvedSelectedTransformBlockIds, sortedBlocks]);
  const selectedImageBrushBlock = useMemo<SceneImageBrushBlock | null>(() => {
    if (!selectedBlockId) {
      return null;
    }

    const block = findSceneBlockById(sortedBlocks, selectedBlockId);
    return block && isSceneImageBrushBlock(block) ? block : null;
  }, [selectedBlockId, sortedBlocks]);
  const imageBrushTargetBlock = selectedImageBrushBlock ?? defaultImageBrushBlock;
  const activeImageRegenerationBlockIdSet = useMemo(
    () => new Set(activeImageRegenerationBlockIds ?? EMPTY_ACTIVE_IMAGE_REGENERATION_BLOCK_IDS),
    [activeImageRegenerationBlockIds],
  );
  const imageBrushToolAvailability = resolveImageBrushToolAvailability({
    targetBlockId: imageBrushTargetBlock?.id ?? null,
    activeRegenerationBlockIds: activeImageRegenerationBlockIdSet,
    hasSubmitHandler: Boolean(onSubmitImageRegeneration),
  });
  const isImageBrushToolActive = workspaceTool === "brush" && Boolean(imageBrushTargetBlock);
  const visibleImageBrushBlock = isImageBrushToolActive
    ? imageBrushTargetBlock
    : selectedImageBrushBlock;

  useEffect(() => {
    setWorkspaceTransform(null);
  }, [
    chromeFrame.height,
    chromeFrame.width,
    plan.scene.canvas.height,
    plan.scene.canvas.width,
    containerWidth,
    containerHeight,
  ]);

  useEffect(() => {
    if (workspaceTool === "brush" && !imageBrushTargetBlock) {
      setWorkspaceTool("edit");
    }
  }, [imageBrushTargetBlock, workspaceTool]);

  useEffect(() => {
    if (workspaceTool !== "brush" || !imageBrushToolAvailability.isRegenerating) {
      return;
    }

    setWorkspaceTool("edit");
    setImageBrushPrompt("");
    setImageBrushStrokes([]);
    setActiveImageBrushStrokeId(null);
  }, [imageBrushToolAvailability.isRegenerating, workspaceTool]);

  useEffect(() => {
    setImageBrushPrompt("");
    setImageBrushStrokes([]);
    setActiveImageBrushStrokeId(null);
  }, [imageBrushTargetBlock?.id]);

  const zoomWorkspace = (factor: number) => {
    setWorkspaceTransform((current) => {
      const transform = current ?? fitTransform;

      return resolveWorkspaceZoomTransform({
        transform,
        nextZoom: transform.zoom * factor,
        origin: lastWorkspacePointerRef.current,
        fallbackOrigin: workspaceCenter,
      });
    });
  };

  const fitWorkspace = () => {
    setWorkspaceTransform(null);
  };

  const getWorkspaceSurfacePointer = (point: { clientX: number; clientY: number }) => {
    const rect = workspaceSurfaceRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }

    return resolveWorkspacePointerFromClientPoint({
      clientX: point.clientX,
      clientY: point.clientY,
      rectLeft: rect.left,
      rectTop: rect.top,
    });
  };

  const zoomWorkspaceAtPointer = (deltaY: number, pointer: { x: number; y: number }) => {
    lastWorkspacePointerRef.current = pointer;
    const zoomFactor = resolveWorkspaceWheelZoomFactor(deltaY);
    if (zoomFactor === 1) {
      return;
    }

    setWorkspaceTransform((current) => {
      const transform = current ?? fitTransform;

      return resolveWorkspaceZoomTransform({
        transform,
        nextZoom: transform.zoom * zoomFactor,
        origin: pointer,
        fallbackOrigin: workspaceCenter,
      });
    });
  };

  const handleWorkspaceWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    if (!event.metaKey && !event.ctrlKey) {
      return;
    }

    const pointer = getWorkspaceSurfacePointer(event);
    if (!pointer) {
      return;
    }

    event.preventDefault();
    if (onWorkspaceWheelZoom) {
      const zoomFactor = resolveWorkspaceWheelZoomFactor(event.deltaY);
      if (zoomFactor !== 1) {
        onWorkspaceWheelZoom(zoomFactor);
      }
      return;
    }

    zoomWorkspaceAtPointer(event.deltaY, pointer);
  };

  const beginWorkspacePan = (pointer: { x: number; y: number }) => {
    lastWorkspacePointerRef.current = pointer;
    setPanDrag({ pointer, transform: activeWorkspaceTransform });
  };

  const beginMarqueeSelection = (pointer: { x: number; y: number }) => {
    if (workspaceTool !== "edit" || isImageBrushToolActive || editingText) {
      return;
    }

    setMarqueeDrag({ anchor: pointer, rect: resolveMarqueeRect(pointer, pointer) });
  };

  const updateMarqueeSelection = (pointer: { x: number; y: number }) => {
    setMarqueeDrag((current) =>
      current ? { ...current, rect: resolveMarqueeRect(current.anchor, pointer) } : current,
    );
  };

  const commitMarqueeSelection = () => {
    if (!marqueeDrag) {
      return;
    }

    const didDrag = marqueeDrag.rect.width >= 4 || marqueeDrag.rect.height >= 4;
    const nextBlockIds = didDrag
      ? resolveMarqueeSelectedBlockIds({
          rect: marqueeDrag.rect,
          blocks: sortedBlocks,
          canvasOffsetX: viewport.canvasOffsetX,
          canvasOffsetY: viewport.canvasOffsetY,
          scale,
        })
      : [];

    setMarqueeDrag(null);
    if (onSelectBlocks) {
      onSelectBlocks(nextBlockIds);
      return;
    }

    onSelectBlock?.(nextBlockIds.at(-1) ?? "");
  };

  const handleWorkspaceMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (workspaceTool !== "pan" || isFormControlTarget(event.target)) {
      return;
    }

    const pointer = getWorkspaceSurfacePointer(event);
    if (!pointer) {
      return;
    }

    event.preventDefault();
    beginWorkspacePan(pointer);
  };

  const handleWorkspaceMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    const pointer = getWorkspaceSurfacePointer(event);
    if (!pointer) {
      return;
    }

    lastWorkspacePointerRef.current = pointer;
    if (!panDrag) {
      return;
    }

    setWorkspaceTransform(
      resolvePannedWorkspaceTransform(panDrag.transform, {
        x: pointer.x - panDrag.pointer.x,
        y: pointer.y - panDrag.pointer.y,
      }),
    );
  };

  const handleWorkspaceTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (workspaceTool !== "pan" || isFormControlTarget(event.target)) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    const pointer = getWorkspaceSurfacePointer(touch);
    if (!pointer) {
      return;
    }

    event.preventDefault();
    beginWorkspacePan(pointer);
  };

  const handleWorkspaceTouchMove = (event: ReactTouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    const pointer = getWorkspaceSurfacePointer(touch);
    if (!pointer) {
      return;
    }

    lastWorkspacePointerRef.current = pointer;
    if (!panDrag) {
      return;
    }

    event.preventDefault();
    setWorkspaceTransform(
      resolvePannedWorkspaceTransform(panDrag.transform, {
        x: pointer.x - panDrag.pointer.x,
        y: pointer.y - panDrag.pointer.y,
      }),
    );
  };

  const getImageBrushPoint = (block: SceneImageBrushBlock) => {
    const localPoint = nodeMapRef.current.get(block.id)?.getRelativePointerPosition();
    if (!localPoint) {
      return null;
    }

    return resolveImageBrushLocalPoint({
      localX: localPoint.x,
      localY: localPoint.y,
      scale,
      width: block.width,
      height: block.height,
    });
  };

  const beginImageBrushStroke = () => {
    if (!imageBrushTargetBlock) {
      return;
    }

    const point = getImageBrushPoint(imageBrushTargetBlock);
    if (!point) {
      return;
    }

    imageBrushStrokeCounterRef.current += 1;
    const strokeId = `image-brush-${imageBrushStrokeCounterRef.current}`;
    setActiveImageBrushStrokeId(strokeId);
    setImageBrushStrokes((current) => [...current, { id: strokeId, points: [point] }]);
  };

  const continueImageBrushStroke = () => {
    if (!imageBrushTargetBlock || !activeImageBrushStrokeId) {
      return;
    }

    const point = getImageBrushPoint(imageBrushTargetBlock);
    if (!point) {
      return;
    }

    setImageBrushStrokes((current) =>
      current.map((stroke) =>
        stroke.id === activeImageBrushStrokeId ? appendImageBrushPoint(stroke, point, 2) : stroke,
      ),
    );
  };

  const submitImageBrushFeedback = async () => {
    if (!canSubmitImageBrushFeedback({ prompt: imageBrushPrompt, strokes: imageBrushStrokes })) {
      return;
    }

    if (
      !imageBrushTargetBlock ||
      !onSubmitImageRegeneration ||
      isImageRegenerationPending ||
      imageBrushToolAvailability.isRegenerating
    ) {
      return;
    }

    const sourceAsset = imageAssetByBlockId.get(imageBrushTargetBlock.id);
    if (!sourceAsset) {
      return;
    }

    const accepted = await onSubmitImageRegeneration({
      blockId: imageBrushTargetBlock.id,
      sourceGeneratedAssetId: sourceAsset.generatedAssetId,
      prompt: imageBrushPrompt.trim(),
      brushRadius: DEFAULT_SOCIAL_POST_IMAGE_REGENERATION_BRUSH_RADIUS,
      strokes: imageBrushStrokes.filter((stroke) => stroke.points.length > 1),
    });

    if (!accepted) {
      return;
    }

    setImageBrushPrompt("");
    setImageBrushStrokes([]);
    setActiveImageBrushStrokeId(null);
  };

  const stageCursorClassName =
    workspaceTool === "pan"
      ? panDrag
        ? "cursor-grabbing"
        : "cursor-grab"
      : isImageBrushToolActive
        ? "cursor-crosshair"
        : "";
  const renderContext: RenderContext = {
    scale,
    canvas: plan.scene.canvas,
    blockById,
    imageAssetByBlockId,
    logoAssets,
    brandkit: brandkit ?? { logo: null, fonts: [] },
    selectedBlockId,
    selectedBlockIds: resolvedSelectedBlockIds,
    selectedTransformBlockId,
    selectedTransformBlockIds: resolvedSelectedTransformBlockIds,
    editingTextBlockId: editingText?.blockId ?? null,
    editingTextValue: editingText?.value ?? null,
    editingTextSelection,
    showTextEditCaret,
    isPanToolActive: workspaceTool === "pan",
    isImageBrushToolActive,
    onSelectBlock,
    onUpdateBlock,
    onUpdateBlocks,
    onStartTextEdit: (blockId) => {
      const block = findSceneBlockById(sortedBlocks, blockId);
      if (block?.type !== "text") {
        return;
      }

      onSelectBlock?.(block.id);
      setEditingText({ blockId: block.id, value: block.text, previousText: block.text });
      setEditingTextSelection({ start: 0, end: block.text.length });
      setShowTextEditCaret(true);
    },
    registeredNodeById: nodeMapRef.current,
    registerNode: (blockId, node) => {
      if (!node) {
        nodeMapRef.current.delete(blockId);
        return;
      }

      nodeMapRef.current.set(blockId, node);
    },
  };

  const editingTextBlock = useMemo(() => {
    if (!editingText) {
      return null;
    }

    const block = findSceneBlockById(sortedBlocks, editingText.blockId);
    return block?.type === "text" ? block : null;
  }, [editingText, sortedBlocks]);

  useEffect(() => {
    if (!editingText?.blockId) {
      return;
    }

    textEditorRef.current?.focus();
    textEditorRef.current?.select();
    setEditingTextSelection({ start: 0, end: textEditorRef.current?.value.length ?? 0 });
  }, [editingText?.blockId]);

  useEffect(() => {
    if (!editingText) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setShowTextEditCaret((visible) => !visible);
    }, TEXT_EDIT_CARET_BLINK_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [editingText]);

  useEffect(() => {
    if (editingText && !editingTextBlock) {
      setEditingText(null);
    }
  }, [editingText, editingTextBlock]);

  useEffect(() => {
    if (!editingText || !editingTextBlock || !editingText.value.trim()) {
      return;
    }

    if (editingText.value === editingTextBlock.text) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      onUpdateBlock?.(editingTextBlock.id, { text: editingText.value });
    }, TEXT_EDIT_SYNC_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [editingText, editingTextBlock, onUpdateBlock]);

  useEffect(() => {
    const transformer = transformerRef.current;
    if (!transformer) {
      return;
    }

    const node = editingText
      ? null
      : resolvedSelectedTransformBlockIds.length === 1
        ? (nodeMapRef.current.get(resolvedSelectedTransformBlockIds[0] ?? "") ?? null)
        : null;
    transformer.nodes(node ? [node] : []);
    transformer.forceUpdate();
    transformer.getLayer()?.batchDraw();
  }, [
    editingText,
    previewHeight,
    previewWidth,
    selectedTransformBlockSnapshot,
    resolvedSelectedTransformBlockIds,
  ]);

  const commitTextEdit = () => {
    if (!editingText || !editingTextBlock) {
      return;
    }

    const value = resolveCommittedTextEdit({
      previousText: editingText.previousText,
      nextText: editingText.value,
    });

    if (value) {
      onUpdateBlock?.(editingTextBlock.id, { text: value });
    } else if (editingTextBlock.text !== editingText.previousText) {
      onUpdateBlock?.(editingTextBlock.id, { text: editingText.previousText });
    }

    setEditingText(null);
    setEditingTextSelection(null);
  };

  const cancelTextEdit = () => {
    if (editingText && editingTextBlock && editingTextBlock.text !== editingText.previousText) {
      onUpdateBlock?.(editingTextBlock.id, { text: editingText.previousText });
    }

    setEditingText(null);
    setEditingTextSelection(null);
  };

  const updateTextEditSelection = (element: HTMLTextAreaElement) => {
    setEditingTextSelection({ start: element.selectionStart, end: element.selectionEnd });
    setShowTextEditCaret(true);
  };

  const editingTextNode = editingTextBlock
    ? (nodeMapRef.current.get(editingTextBlock.id) ?? null)
    : null;
  const editingTextPosition = editingTextNode?.getAbsolutePosition();
  const editingTextScale = editingTextNode?.getAbsoluteScale();
  const editingTextOverlayFrame = editingTextBlock
    ? resolveTextEditorOverlayFrame({
        absoluteX: editingTextPosition?.x ?? viewport.canvasOffsetX + editingTextBlock.x * scale,
        absoluteY: editingTextPosition?.y ?? viewport.canvasOffsetY + editingTextBlock.y * scale,
        width: editingTextBlock.width * scale,
        height: editingTextBlock.height * scale,
        scaleX: editingTextScale?.x ?? 1,
        scaleY: editingTextScale?.y ?? 1,
        rotation: editingTextNode?.getAbsoluteRotation() ?? editingTextBlock.rotation,
      })
    : null;
  const workspaceControls = showWorkspaceControls ? (
    <WorkspaceControls
      tool={workspaceTool}
      canUseBrush={imageBrushToolAvailability.canUseBrush}
      isBrushRegenerationInFlight={imageBrushToolAvailability.isRegenerating}
      zoomLabel={workspaceZoomLabel ?? `${Math.round(activeWorkspaceTransform.zoom * 100)}%`}
      canUndo={canUndo}
      canRedo={canRedo}
      onUndo={onUndo}
      onRedo={onRedo}
      onSelectTool={setWorkspaceTool}
      onZoomOut={onWorkspaceZoomOut ?? (() => zoomWorkspace(0.85))}
      onFitView={onWorkspaceFitView ?? fitWorkspace}
      onZoomIn={onWorkspaceZoomIn ?? (() => zoomWorkspace(1.15))}
    />
  ) : null;

  return (
    <div
      ref={containerRef}
      className={[
        artboard
          ? "flex h-auto min-h-0 w-full flex-col overflow-visible"
          : "flex h-full min-h-[34rem] w-full flex-col overflow-hidden",
        flat || artboard
          ? "bg-transparent"
          : "rounded-[2rem] border border-border/70 bg-[#fffdf8] shadow-[0_26px_70px_rgba(19,34,49,0.14)]",
      ].join(" ")}
    >
      <div
        ref={workspaceSurfaceRef}
        className={[
          "relative flex w-full items-center justify-center",
          artboard ? "overflow-visible" : "flex-1 overflow-hidden",
          flat || artboard ? "bg-transparent" : "bg-[#fffdf8]",
        ].join(" ")}
        onWheel={handleWorkspaceWheel}
        onMouseDown={handleWorkspaceMouseDown}
        onMouseMove={handleWorkspaceMouseMove}
        onMouseUp={() => setPanDrag(null)}
        onMouseLeave={() => {
          lastWorkspacePointerRef.current = null;
          setPanDrag(null);
        }}
        onTouchStart={handleWorkspaceTouchStart}
        onTouchMove={handleWorkspaceTouchMove}
        onTouchEnd={() => setPanDrag(null)}
        onTouchCancel={() => setPanDrag(null)}
        style={{
          minHeight: artboard ? workspaceHeight : undefined,
          backgroundImage: artboard
            ? undefined
            : "radial-gradient(circle at 50% 14%, rgba(255,241,212,0.38), transparent 24rem), radial-gradient(circle at 18% 84%, rgba(255,241,212,0.42), transparent 18rem), linear-gradient(rgba(19,34,49,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(19,34,49,0.04) 1px, transparent 1px), radial-gradient(circle at 1px 1px, rgba(19,34,49,0.09) 1px, transparent 0)",
          backgroundPosition: artboard ? undefined : "-1px -1px, -1px -1px, 0 0",
          backgroundSize: artboard ? undefined : "auto, auto, 96px 96px, 96px 96px, 12px 12px",
        }}
      >
        <div
          className="pointer-events-none absolute left-0 top-0 z-0 origin-top-left"
          style={{
            width: chromeFrame.width,
            height: chromeFrame.height,
            transform: `translate(${activeWorkspaceTransform.x}px, ${activeWorkspaceTransform.y}px) scale(${activeWorkspaceTransform.zoom})`,
          }}
          aria-hidden="true"
        >
          <InstagramPostChrome
            platform={plan.platform}
            postType={plan.postType}
            profileLabel={resolvedProfileLabel}
            caption={caption}
            hashtags={hashtags}
            showMetadata={!(onCaptionChange || onHashtagsChange)}
            media={
              <div
                className="bg-black/5"
                style={{ width: plan.scene.canvas.width, height: plan.scene.canvas.height }}
              />
            }
          />
        </div>
        <div className={["relative z-10 shrink-0", stageCursorClassName].filter(Boolean).join(" ")}>
          <Stage
            width={viewport.width}
            height={viewport.height}
            onMouseDown={(event) => {
              const pointer = getWorkspacePointer(event);
              if (pointer) {
                lastWorkspacePointerRef.current = pointer;
              }

              if (workspaceTool === "pan") {
                return;
              }

              if (isImageBrushToolActive) {
                beginImageBrushStroke();
                return;
              }

              if (event.target === event.target.getStage()) {
                if (pointer) {
                  beginMarqueeSelection(pointer);
                }
              }
            }}
            onMouseMove={(event) => {
              const pointer = getWorkspacePointer(event);
              if (!pointer) {
                return;
              }

              lastWorkspacePointerRef.current = pointer;

              if (isImageBrushToolActive) {
                continueImageBrushStroke();
                return;
              }

              if (marqueeDrag) {
                updateMarqueeSelection(pointer);
              }
            }}
            onMouseUp={() => {
              commitMarqueeSelection();
              setActiveImageBrushStrokeId(null);
            }}
            onMouseLeave={() => {
              setMarqueeDrag(null);
              setActiveImageBrushStrokeId(null);
            }}
            onTouchStart={(event) => {
              const pointer = getWorkspacePointer(event);
              if (pointer) {
                lastWorkspacePointerRef.current = pointer;
              }

              if (workspaceTool === "pan") {
                return;
              }

              if (isImageBrushToolActive) {
                event.evt.preventDefault();
                beginImageBrushStroke();
                return;
              }

              if (event.target === event.target.getStage()) {
                onSelectBlock?.("");
              }
            }}
            onTouchMove={(event) => {
              const pointer = getWorkspacePointer(event);
              if (!pointer) {
                return;
              }

              lastWorkspacePointerRef.current = pointer;

              if (isImageBrushToolActive) {
                event.evt.preventDefault();
                continueImageBrushStroke();
                return;
              }
            }}
            onTouchEnd={() => {
              setActiveImageBrushStrokeId(null);
            }}
          >
            <Layer>
              <KonvaRect
                x={viewport.canvasOffsetX}
                y={viewport.canvasOffsetY}
                width={previewWidth}
                height={previewHeight}
                fill={plan.scene.canvas.backgroundFill}
                shadowColor="rgba(44,44,42,0.12)"
                shadowBlur={24}
                shadowOffsetY={10}
                shadowOpacity={0.65}
                onMouseDown={(event) => {
                  if (workspaceTool === "edit") {
                    const pointer = getWorkspacePointer(event);
                    if (pointer) {
                      lastWorkspacePointerRef.current = pointer;
                      beginMarqueeSelection(pointer);
                    }
                  }
                }}
                onTouchStart={() => {
                  if (workspaceTool === "edit") {
                    onSelectBlock?.("");
                  }
                }}
              />
              <KonvaGroup x={viewport.canvasOffsetX} y={viewport.canvasOffsetY}>
                {sortedBlocks.map((block) => (
                  <BlockNode
                    key={block.id}
                    block={block}
                    context={renderContext}
                    selectedBlockId={selectedBlockId}
                    selectedBlockIds={resolvedSelectedBlockIds}
                    selectedTransformBlockId={selectedTransformBlockId}
                    selectedTransformBlockIds={resolvedSelectedTransformBlockIds}
                    onSelectBlock={onSelectBlock}
                  />
                ))}
                {visibleImageBrushBlock ? (
                  <ImageBrushSelectionOverlay
                    block={visibleImageBrushBlock}
                    scale={scale}
                    strokes={imageBrushStrokes}
                  />
                ) : null}
              </KonvaGroup>
              <StageOverflowMask
                viewportWidth={viewport.width}
                viewportHeight={viewport.height}
                stageX={viewport.canvasOffsetX}
                stageY={viewport.canvasOffsetY}
                stageWidth={previewWidth}
                stageHeight={previewHeight}
              />
              <KonvaRect
                x={viewport.canvasOffsetX}
                y={viewport.canvasOffsetY}
                width={previewWidth}
                height={previewHeight}
                stroke="#d8d3c9"
                strokeWidth={1}
                listening={false}
              />
              {marqueeDrag ? (
                <KonvaRect
                  x={marqueeDrag.rect.x}
                  y={marqueeDrag.rect.y}
                  width={marqueeDrag.rect.width}
                  height={marqueeDrag.rect.height}
                  fill="rgba(91, 240, 151, 0.12)"
                  stroke="#5bf097"
                  strokeWidth={1}
                  dash={[6, 4]}
                  listening={false}
                />
              ) : null}
              <Transformer
                ref={transformerRef}
                visible={!editingText && !isImageBrushToolActive}
                rotateEnabled={true}
                flipEnabled={false}
                rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                boundBoxFunc={(previousBox, nextBox) =>
                  resolveTransformerBoundBox(previousBox, nextBox, 8 * scale)
                }
                borderStroke="#5bf097"
                anchorStroke="#5bf097"
                anchorFill="#ffffff"
                anchorSize={10}
                rotateAnchorOffset={28}
                borderDash={[6, 4]}
                ignoreStroke={true}
              />
            </Layer>
          </Stage>
          {editingText && editingTextBlock ? (
            <textarea
              ref={textEditorRef}
              value={editingText.value}
              onChange={(event) =>
                setEditingText({
                  blockId: editingText.blockId,
                  previousText: editingText.previousText,
                  value: event.target.value,
                })
              }
              onInput={(event) => updateTextEditSelection(event.currentTarget)}
              onSelect={(event) => updateTextEditSelection(event.currentTarget)}
              onKeyUp={(event) => updateTextEditSelection(event.currentTarget)}
              onMouseUp={(event) => updateTextEditSelection(event.currentTarget)}
              onBlur={commitTextEdit}
              onKeyDown={(event) => {
                const action = resolveTextEditKeyAction({
                  key: event.key,
                  shiftKey: event.shiftKey,
                  isComposing: event.nativeEvent.isComposing,
                });

                if (action === "cancel") {
                  event.preventDefault();
                  cancelTextEdit();
                  return;
                }

                if (action === "commit") {
                  event.preventDefault();
                  commitTextEdit();
                }
              }}
              aria-label={intl.formatMessage({
                id: "campaignCreativeScenePreview.editCreativeText",
                defaultMessage: "Edit creative text",
              })}
              className="absolute z-10 resize-none overflow-hidden border-0 bg-transparent p-0 text-transparent opacity-0 outline-none ring-0"
              style={{
                left: editingTextOverlayFrame?.left,
                top: editingTextOverlayFrame?.top,
                width: editingTextOverlayFrame?.width,
                height: editingTextOverlayFrame?.height,
                opacity: 0,
                color: editingTextBlock.fill,
                fontFamily: editingTextBlock.fontFamily,
                fontSize: editingTextBlock.fontSize * scale,
                fontStyle: editingTextBlock.fontStyle.includes("italic") ? "italic" : "normal",
                fontWeight: editingTextBlock.fontStyle.includes("bold") ? 700 : 400,
                lineHeight: editingTextBlock.lineHeight,
                letterSpacing: editingTextBlock.letterSpacing * scale,
                padding: editingTextBlock.padding * scale,
                textAlign: editingTextBlock.align,
                caretColor: editingTextBlock.fill,
                transform: editingTextOverlayFrame?.transform,
                transformOrigin: editingTextOverlayFrame?.transformOrigin,
              }}
            />
          ) : null}
        </div>
        {onCaptionChange || onHashtagsChange ? (
          <div
            className="pointer-events-none absolute left-0 top-0 z-20 origin-top-left"
            style={{
              width: chromeFrame.width,
              height: chromeFrame.height,
              transform: `translate(${activeWorkspaceTransform.x}px, ${activeWorkspaceTransform.y}px) scale(${activeWorkspaceTransform.zoom})`,
            }}
          >
            <InstagramPostMetadataEditor
              variant={chromeVariant}
              profileLabel={resolvedProfileLabel}
              mediaWidth={plan.scene.canvas.width}
              mediaHeight={plan.scene.canvas.height}
              caption={caption}
              hashtags={hashtags}
              onCaptionChange={onCaptionChange}
              onHashtagsChange={onHashtagsChange}
            />
          </div>
        ) : onCaptionDoubleClick ? (
          <div
            className="pointer-events-none absolute left-0 top-0 z-20 origin-top-left"
            style={{
              width: chromeFrame.width,
              height: chromeFrame.height,
              transform: `translate(${activeWorkspaceTransform.x}px, ${activeWorkspaceTransform.y}px) scale(${activeWorkspaceTransform.zoom})`,
            }}
          >
            <div
              className="pointer-events-auto absolute cursor-pointer"
              style={{
                left: 0,
                top: 84 + plan.scene.canvas.height + 64,
                width: plan.scene.canvas.width + 96,
                height: chromeFrame.height - (84 + plan.scene.canvas.height + 64),
              }}
              onClick={onCaptionDoubleClick}
            />
          </div>
        ) : null}
        {workspaceControlsPortal && workspaceControls
          ? createPortal(workspaceControls, workspaceControlsPortal)
          : workspaceControls}
        {isImageBrushToolActive ? (
          <ImageBrushFeedbackPanel
            prompt={imageBrushPrompt}
            strokeCount={imageBrushStrokes.filter((stroke) => stroke.points.length > 1).length}
            canSubmit={canSubmitImageBrushFeedback({
              prompt: imageBrushPrompt,
              strokes: imageBrushStrokes,
            })}
            hasSourceImage={Boolean(
              imageBrushTargetBlock && imageAssetByBlockId.has(imageBrushTargetBlock.id),
            )}
            isSubmitting={isImageRegenerationPending}
            error={imageRegenerationError}
            onPromptChange={setImageBrushPrompt}
            onClear={() => {
              setImageBrushStrokes([]);
              setActiveImageBrushStrokeId(null);
            }}
            onSubmit={submitImageBrushFeedback}
          />
        ) : null}
      </div>
      {flat ? null : (
        <div className="flex shrink-0 items-center justify-between border-t border-border/70 bg-[#fffdf8]/95 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.16em] text-foreground/42 tabular-nums">
          <span>
            {plan.scene.canvas.width}
            {" × "}
            {plan.scene.canvas.height}px
          </span>
          <span>{Math.round(activeWorkspaceTransform.zoom * 100)}%</span>
        </div>
      )}
    </div>
  );
}

function getWorkspaceToolButtonClassName(active: boolean) {
  return [
    "flex h-9 w-9 items-center justify-center rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50",
    active
      ? "bg-foreground text-primary-foreground shadow-[0_8px_22px_rgba(19,34,49,0.16)]"
      : "text-foreground/58 hover:bg-muted/80 hover:text-foreground",
  ].join(" ");
}

function WorkspaceControls({
  tool,
  canUseBrush,
  isBrushRegenerationInFlight,
  zoomLabel,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSelectTool,
  onZoomOut,
  onFitView,
  onZoomIn,
}: {
  tool: WorkspaceTool;
  canUseBrush: boolean;
  isBrushRegenerationInFlight: boolean;
  zoomLabel: string;
  canUndo: boolean;
  canRedo: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onSelectTool: (tool: WorkspaceTool) => void;
  onZoomOut: () => void;
  onFitView: () => void;
  onZoomIn: () => void;
}) {
  const intl = useIntl();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      )
        return;

      const cmd = e.metaKey || e.ctrlKey;

      if (!cmd && e.key === "v") {
        e.preventDefault();
        onSelectTool("edit");
        return;
      }
      if (!cmd && e.key === "h") {
        e.preventDefault();
        onSelectTool("pan");
        return;
      }
      if (!cmd && e.key === "b" && canUseBrush) {
        e.preventDefault();
        onSelectTool("brush");
        return;
      }
      if (!cmd && (e.key === "-" || e.key === "_")) {
        e.preventDefault();
        onZoomOut();
        return;
      }
      if (!cmd && (e.key === "=" || e.key === "+")) {
        e.preventDefault();
        onZoomIn();
        return;
      }
      if (cmd && e.key === "0") {
        e.preventDefault();
        onFitView();
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canUseBrush, onSelectTool, onZoomOut, onZoomIn, onFitView]);

  const iconBtn =
    "flex h-9 w-9 items-center justify-center rounded-xl text-foreground/58 transition-colors hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 disabled:cursor-not-allowed disabled:opacity-30";

  const brushBtn = [
    "flex h-9 items-center gap-1.5 rounded-xl border px-3 text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 disabled:cursor-not-allowed disabled:opacity-30",
    tool === "brush"
      ? "border-foreground/20 bg-foreground text-primary-foreground shadow-[0_8px_22px_rgba(19,34,49,0.16)]"
      : "border-border/70 bg-background text-foreground/64 hover:bg-muted/80 hover:text-foreground",
  ].join(" ");

  return (
    <div className="pointer-events-auto absolute left-1/2 top-4 z-20 -translate-x-1/2">
      <div className="flex items-center gap-1 rounded-2xl border border-border/70 bg-[#fffdf8]/90 p-1.5 shadow-[0_18px_44px_rgba(19,34,49,0.16)] backdrop-blur-xl">
        {/* Undo / Redo */}
        <button
          type="button"
          title={intl.formatMessage({
            id: "campaignCreativeScenePreview.undoShortcut",
            defaultMessage: "Undo (⌘Z)",
          })}
          className={iconBtn}
          aria-label={intl.formatMessage({
            id: "campaignCreativeScenePreview.undoLastEdit",
            defaultMessage: "Undo last edit",
          })}
          disabled={!canUndo}
          onClick={onUndo}
        >
          <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          title={intl.formatMessage({
            id: "campaignCreativeScenePreview.redoShortcut",
            defaultMessage: "Redo (⌘⇧Z)",
          })}
          className={iconBtn}
          aria-label={intl.formatMessage({
            id: "campaignCreativeScenePreview.redoLastEdit",
            defaultMessage: "Redo last edit",
          })}
          disabled={!canRedo}
          onClick={onRedo}
        >
          <Redo2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>

        <span className="mx-0.5 h-5 w-px bg-border/80" aria-hidden="true" />

        {/* Tool select */}
        <button
          type="button"
          title={intl.formatMessage({
            id: "campaignCreativeScenePreview.selectShortcut",
            defaultMessage: "Select (V)",
          })}
          className={getWorkspaceToolButtonClassName(tool === "edit")}
          aria-label={intl.formatMessage({
            id: "campaignCreativeScenePreview.editLayers",
            defaultMessage: "Edit layers",
          })}
          aria-pressed={tool === "edit"}
          onClick={() => onSelectTool("edit")}
        >
          <MousePointer2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          title={intl.formatMessage({
            id: "campaignCreativeScenePreview.panShortcut",
            defaultMessage: "Pan (H)",
          })}
          className={getWorkspaceToolButtonClassName(tool === "pan")}
          aria-label={intl.formatMessage({
            id: "campaignCreativeScenePreview.panWorkspace",
            defaultMessage: "Pan workspace",
          })}
          aria-pressed={tool === "pan"}
          onClick={() => onSelectTool("pan")}
        >
          <Hand className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          title={intl.formatMessage(
            isBrushRegenerationInFlight
              ? {
                  id: "campaignCreativeScenePreview.imageRegenerationInProgress",
                  defaultMessage: "Image regeneration in progress",
                }
              : {
                  id: "campaignCreativeScenePreview.brushShortcut",
                  defaultMessage: "Regenerate with brush (B)",
                },
          )}
          className={brushBtn}
          aria-label={intl.formatMessage(
            isBrushRegenerationInFlight
              ? {
                  id: "campaignCreativeScenePreview.imageRegenerationInProgress",
                  defaultMessage: "Image regeneration in progress",
                }
              : {
                  id: "campaignCreativeScenePreview.paintImageFeedbackSelection",
                  defaultMessage: "Paint image feedback selection",
                },
          )}
          aria-pressed={tool === "brush"}
          disabled={!canUseBrush}
          onClick={() => onSelectTool("brush")}
        >
          {isBrushRegenerationInFlight ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Brush className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {intl.formatMessage(
            isBrushRegenerationInFlight
              ? {
                  id: "campaignCreativeScenePreview.regenerating",
                  defaultMessage: "Regenerating…",
                }
              : {
                  id: "campaignCreativeScenePreview.brush",
                  defaultMessage: "Brush",
                },
          )}
        </button>

        <span className="mx-0.5 h-5 w-px bg-border/80" aria-hidden="true" />

        {/* Zoom */}
        <button
          type="button"
          title={intl.formatMessage({
            id: "campaignCreativeScenePreview.zoomOutShortcut",
            defaultMessage: "Zoom out (−)",
          })}
          className={iconBtn}
          aria-label={intl.formatMessage({
            id: "campaignCreativeScenePreview.zoomOut",
            defaultMessage: "Zoom out",
          })}
          onClick={onZoomOut}
        >
          <ZoomOut className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          title={intl.formatMessage({
            id: "campaignCreativeScenePreview.fitShortcut",
            defaultMessage: "Fit to window (⌘0)",
          })}
          className="flex h-9 min-w-14 items-center justify-center rounded-xl px-2 text-[11px] font-semibold tabular-nums text-foreground/62 transition-colors hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
          aria-label={intl.formatMessage({
            id: "campaignCreativeScenePreview.fitCreativeToWorkspace",
            defaultMessage: "Fit creative to workspace",
          })}
          onClick={onFitView}
        >
          {zoomLabel}
        </button>
        <button
          type="button"
          title={intl.formatMessage({
            id: "campaignCreativeScenePreview.zoomInShortcut",
            defaultMessage: "Zoom in (+)",
          })}
          className={iconBtn}
          aria-label={intl.formatMessage({
            id: "campaignCreativeScenePreview.zoomIn",
            defaultMessage: "Zoom in",
          })}
          onClick={onZoomIn}
        >
          <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function ImageBrushFeedbackPanel({
  prompt,
  strokeCount,
  canSubmit,
  hasSourceImage,
  isSubmitting,
  error,
  onPromptChange,
  onClear,
  onSubmit,
}: {
  prompt: string;
  strokeCount: number;
  canSubmit: boolean;
  hasSourceImage: boolean;
  isSubmitting: boolean;
  error: string | null;
  onPromptChange: (value: string) => void;
  onClear: () => void;
  onSubmit: () => void | Promise<void>;
}) {
  const intl = useIntl();
  const isSubmitDisabled = !canSubmit || !hasSourceImage || isSubmitting;

  return (
    <form
      className="absolute bottom-4 left-4 z-20 w-[min(28rem,calc(100%-2rem))] rounded-3xl border border-border/70 bg-[#fffdf8]/92 p-4 text-foreground shadow-[0_24px_70px_rgba(19,34,49,0.16)] backdrop-blur-xl"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground/70">
            {intl.formatMessage({
              id: "campaignCreativeScenePreview.imageFeedback",
              defaultMessage: "Image feedback",
            })}
          </div>
          <div className="mt-0.5 text-xs text-foreground/45">
            {intl.formatMessage(
              {
                id: "campaignCreativeScenePreview.paintedSelectionCount",
                defaultMessage:
                  "{count, plural, one {# painted selection} other {# painted selections}}",
              },
              { count: strokeCount },
            )}
          </div>
        </div>
        <button
          type="button"
          className="rounded-full border border-border/70 px-3 py-1 text-xs font-medium text-foreground/62 hover:bg-muted/70 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          disabled={strokeCount === 0}
          onClick={onClear}
        >
          {intl.formatMessage({ id: "common.clear", defaultMessage: "Clear" })}
        </button>
      </div>
      <label className="sr-only" htmlFor="image-brush-feedback-prompt">
        {intl.formatMessage({
          id: "campaignCreativeScenePreview.imageFeedbackPrompt",
          defaultMessage: "Image feedback prompt",
        })}
      </label>
      <textarea
        id="image-brush-feedback-prompt"
        value={prompt}
        onChange={(event) => onPromptChange(event.target.value)}
        rows={3}
        placeholder={intl.formatMessage({
          id: "campaignCreativeScenePreview.imageFeedbackPlaceholder",
          defaultMessage: "Describe what should change in the painted area",
        })}
        className="min-h-20 w-full resize-none rounded-2xl border border-border/70 bg-background/80 px-3 py-2 text-sm text-foreground outline-none transition placeholder:text-foreground/34 focus:border-foreground/30 focus:ring-2 focus:ring-brand/25"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-xs leading-5 text-foreground/45">
          {hasSourceImage
            ? intl.formatMessage({
                id: "campaignCreativeScenePreview.regeneratesPaintedPart",
                defaultMessage: "Regenerates only the painted part of this image.",
              })
            : intl.formatMessage({
                id: "campaignCreativeScenePreview.selectGeneratedImageLayer",
                defaultMessage: "Select a generated image layer before submitting.",
              })}
        </p>
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background shadow-sm transition hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-35"
          disabled={isSubmitDisabled}
        >
          {isSubmitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : null}
          {intl.formatMessage({
            id: "campaignCreativeScenePreview.regeneratePaintedArea",
            defaultMessage: "Regenerate area",
          })}
        </button>
      </div>
      {error ? <p className="mt-2 text-xs leading-5 text-destructive">{error}</p> : null}
    </form>
  );
}

function ImageBrushSelectionOverlay({
  block,
  scale,
  strokes,
}: {
  block: SceneImageBrushBlock;
  scale: number;
  strokes: ImageBrushStroke[];
}) {
  if (strokes.length === 0) {
    return null;
  }

  return (
    <KonvaGroup
      x={block.x * scale}
      y={block.y * scale}
      rotation={block.rotation}
      clipX={0}
      clipY={0}
      clipWidth={block.width * scale}
      clipHeight={block.height * scale}
      listening={false}
    >
      {strokes.map((stroke) => {
        const points = stroke.points.flatMap((point) => [point.x * scale, point.y * scale]);
        if (points.length < 4) {
          return null;
        }

        return (
          <KonvaLine
            key={stroke.id}
            points={points}
            stroke="rgba(91, 240, 151, 0.78)"
            strokeWidth={Math.max(14, 34 * scale)}
            lineCap="round"
            lineJoin="round"
            opacity={0.72}
            shadowColor="rgba(19, 126, 71, 0.35)"
            shadowBlur={12}
          />
        );
      })}
    </KonvaGroup>
  );
}

function StageOverflowMask({
  viewportWidth,
  viewportHeight,
  stageX,
  stageY,
  stageWidth,
  stageHeight,
}: {
  viewportWidth: number;
  viewportHeight: number;
  stageX: number;
  stageY: number;
  stageWidth: number;
  stageHeight: number;
}) {
  const fill = "rgba(244, 242, 237, 0.66)";
  const rightX = stageX + stageWidth;
  const bottomY = stageY + stageHeight;

  return (
    <>
      <KonvaRect x={0} y={0} width={viewportWidth} height={stageY} fill={fill} listening={false} />
      <KonvaRect
        x={0}
        y={bottomY}
        width={viewportWidth}
        height={Math.max(0, viewportHeight - bottomY)}
        fill={fill}
        listening={false}
      />
      <KonvaRect
        x={0}
        y={stageY}
        width={stageX}
        height={stageHeight}
        fill={fill}
        listening={false}
      />
      <KonvaRect
        x={rightX}
        y={stageY}
        width={Math.max(0, viewportWidth - rightX)}
        height={stageHeight}
        fill={fill}
        listening={false}
      />
    </>
  );
}

function isFormControlTarget(target: EventTarget): boolean {
  return target instanceof HTMLElement
    ? Boolean(target.closest("input, textarea, select, button, [contenteditable='true']"))
    : false;
}

function useSceneBrandkitFonts(brandkit: CampaignCreativeBrandkit | null): void {
  useEffect(() => {
    if (!brandkit || typeof document === "undefined") {
      return;
    }

    let cancelled = false;

    const load = async () => {
      for (const font of brandkit.fonts) {
        if (cancelled) {
          return;
        }

        if (font.source === "brand_confirmed") {
          const face = new FontFace(font.family, `url(${font.loadUrl})`);
          try {
            const loaded = await face.load();
            document.fonts.add(loaded);
          } catch {
            // best effort
          }
          continue;
        }

        if (!document.querySelector(`link[data-font-url="${font.loadUrl}"]`)) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = font.loadUrl;
          link.dataset.fontUrl = font.loadUrl;
          document.head.append(link);
        }

        const weight = Number.parseInt(font.selectedVariant, 10);

        try {
          await document.fonts.load(
            `${Number.isFinite(weight) ? weight : 400} 16px ${toCssFontFamily(font.family)}`,
          );
        } catch {
          // best effort
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [brandkit]);
}

function findSceneBlockById(blocks: SceneBlock[], blockId: string): SceneBlock | null {
  for (const block of blocks) {
    if (block.id === blockId) {
      return block;
    }
  }

  return null;
}

function BlockNode({
  block,
  context,
  selectedBlockId,
  selectedBlockIds,
  selectedTransformBlockId,
  selectedTransformBlockIds,
  onSelectBlock,
}: {
  block: SceneBlock;
  context: RenderContext;
  selectedBlockId?: string | null;
  selectedBlockIds?: string[];
  selectedTransformBlockId?: string | null;
  selectedTransformBlockIds?: string[];
  onSelectBlock?: (blockId: string, options?: SelectBlockOptions) => void;
}): ReactElement | null {
  if (!block.visible) {
    return null;
  }

  const isSelected = (selectedBlockIds ?? (selectedBlockId ? [selectedBlockId] : [])).includes(
    block.id,
  );
  const selectedIds = context.selectedTransformBlockIds ?? [];
  const isTransformSelected = (
    selectedTransformBlockIds ?? (selectedTransformBlockId ? [selectedTransformBlockId] : [])
  ).includes(block.id);
  const isEditingText = context.editingTextBlockId === block.id;
  const isPanToolActive = context.isPanToolActive ?? false;
  const blockInteraction = resolveBlockInteractionState({
    canUpdateBlock: Boolean(context.onUpdateBlock),
    isTransformSelected,
    isPanToolActive,
    isImageBrushToolActive: context.isImageBrushToolActive ?? false,
  });
  const renderedBlock =
    block.type === "text" && isEditingText && context.editingTextValue != null
      ? { ...block, text: context.editingTextValue }
      : block;

  return (
    <KonvaGroup
      ref={(node) => context.registerNode?.(block.id, node)}
      x={block.x * context.scale}
      y={block.y * context.scale}
      rotation={block.rotation}
      opacity={block.opacity}
      draggable={blockInteraction.canDrag}
      onMouseDown={(event) => {
        if (!blockInteraction.shouldCapturePointerEvents) {
          return;
        }

        event.cancelBubble = true;
        if (isSelected && selectedIds.length > 1 && !event.evt.metaKey && !event.evt.ctrlKey) {
          return;
        }

        onSelectBlock?.(block.id, { toggle: event.evt.metaKey || event.evt.ctrlKey });
      }}
      onTouchStart={(event) => {
        if (!blockInteraction.shouldCapturePointerEvents) {
          return;
        }

        event.cancelBubble = true;
        onSelectBlock?.(block.id);
      }}
      onClick={(event) => {
        if (!blockInteraction.shouldCapturePointerEvents) {
          return;
        }

        event.cancelBubble = true;
      }}
      onDblClick={(event) => {
        if (!blockInteraction.shouldCapturePointerEvents) {
          return;
        }

        event.cancelBubble = true;
        if (block.type === "text") {
          context.onStartTextEdit?.(block.id);
        }
      }}
      onTap={(event) => {
        if (!blockInteraction.shouldCapturePointerEvents) {
          return;
        }

        event.cancelBubble = true;
        onSelectBlock?.(block.id);
      }}
      onDblTap={(event) => {
        if (!blockInteraction.shouldCapturePointerEvents) {
          return;
        }

        event.cancelBubble = true;
        if (block.type === "text") {
          context.onStartTextEdit?.(block.id);
        }
      }}
      onDragStart={(event) => {
        if (!blockInteraction.canDrag) {
          event.target.stopDrag();
          return;
        }

        if (!isSelected) {
          onSelectBlock?.(block.id);
        }
      }}
      onDragMove={(event) => {
        if (!isTransformSelected || selectedIds.length <= 1) {
          return;
        }

        const target = event.target;
        const deltaX = target.x() - block.x * context.scale;
        const deltaY = target.y() - block.y * context.scale;

        for (const blockId of selectedIds) {
          if (blockId === block.id) {
            continue;
          }

          const selectedBlock = context.blockById.get(blockId);
          const node = selectedBlock ? context.registeredNodeById?.get(blockId) : null;
          if (!selectedBlock || !node) {
            continue;
          }

          node.x(selectedBlock.x * context.scale + deltaX);
          node.y(selectedBlock.y * context.scale + deltaY);
        }

        target.getLayer()?.batchDraw();
      }}
      onDragEnd={(event) => {
        const target = event.target;
        const nextPosition = resolveBlockDragPosition({
          usesLocalWrapper: true,
          blockX: block.x,
          blockY: block.y,
          dragX: target.x(),
          dragY: target.y(),
          scale: context.scale,
        });
        if (isTransformSelected && selectedIds.length > 1 && context.onUpdateBlocks) {
          const deltaX = nextPosition.x - block.x;
          const deltaY = nextPosition.y - block.y;
          context.onUpdateBlocks(
            selectedIds.flatMap((blockId) => {
              const selectedBlock = context.blockById.get(blockId);
              return selectedBlock
                ? [
                    {
                      blockId,
                      changes: { x: selectedBlock.x + deltaX, y: selectedBlock.y + deltaY },
                    },
                  ]
                : [];
            }),
          );
        } else {
          context.onUpdateBlock?.(block.id, nextPosition);
        }
        target.getLayer()?.batchDraw();
      }}
      onTransformEnd={(event) => {
        const target = event.target;
        const scaleX = target.scaleX();
        const scaleY = target.scaleY();
        const changes = {
          ...resolveBlockTransformFrame({
            x: target.x(),
            y: target.y(),
            width: block.width * context.scale * scaleX,
            height: block.height * context.scale * scaleY,
            scale: context.scale,
          }),
          rotation: target.rotation(),
        };

        context.onUpdateBlock?.(block.id, changes);

        target.scaleX(1);
        target.scaleY(1);
        target.getLayer()?.batchDraw();
      }}
    >
      <KonvaRect
        x={0}
        y={0}
        width={block.width * context.scale}
        height={block.height * context.scale}
        fill="rgba(0,0,0,0.001)"
        strokeEnabled={false}
        listening={shouldUseBlockBoundsHitArea(block)}
      />
      {renderedBlock.type === "text" ? (
        <>
          {isEditingText && context.editingTextSelection ? (
            <TextEditOverlay
              block={renderedBlock}
              mode="selection"
              scale={context.scale}
              selection={context.editingTextSelection}
              showCaret={context.showTextEditCaret ?? true}
            />
          ) : null}
          <SocialPostSceneBlockContent
            block={renderedBlock}
            scale={context.scale}
            imageAssetByBlockId={context.imageAssetByBlockId}
            brandkit={context.brandkit}
            logoAssets={context.logoAssets}
          />
          {isEditingText && context.editingTextSelection ? (
            <TextEditOverlay
              block={renderedBlock}
              mode="caret"
              scale={context.scale}
              selection={context.editingTextSelection}
              showCaret={context.showTextEditCaret ?? true}
            />
          ) : null}
        </>
      ) : (
        <SocialPostSceneBlockContent
          block={renderedBlock}
          scale={context.scale}
          imageAssetByBlockId={context.imageAssetByBlockId}
          brandkit={context.brandkit}
          logoAssets={context.logoAssets}
        />
      )}
      {isSelected && !context.editingTextBlockId ? (
        <KonvaRect
          x={0}
          y={0}
          width={block.width * context.scale}
          height={block.height * context.scale}
          stroke="#5bf097"
          strokeWidth={2}
          dash={[8, 6]}
          listening={false}
        />
      ) : null}
    </KonvaGroup>
  );
}

function TextEditOverlay({
  block,
  mode,
  scale,
  selection,
  showCaret,
}: {
  block: SceneTextBlock;
  mode: "selection" | "caret";
  scale: number;
  selection: TextEditSelection;
  showCaret: boolean;
}): ReactElement {
  const fontSize = block.fontSize * scale;
  const letterSpacing = block.letterSpacing * scale;
  const padding = block.padding * scale;
  const measureText = useMemo(
    () => createCanvasTextMeasurer(block.fontFamily, block.fontStyle, fontSize),
    [block.fontFamily, block.fontStyle, fontSize],
  );
  const layout = resolveTextEditOverlayLayout({
    text: block.text,
    width: block.width * scale,
    height: block.height * scale,
    fontSize,
    lineHeight: block.lineHeight,
    letterSpacing,
    padding,
    align: block.align === "justify" ? "left" : block.align,
    verticalAlign: block.verticalAlign,
    selectionStart: selection.start,
    selectionEnd: selection.end,
    measureText,
  });

  return (
    <>
      {mode === "selection"
        ? layout.selectionRects.map((rect, index) => (
            <KonvaRect
              key={`selection-${index}`}
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              fill="#5bf097"
              opacity={0.28}
              listening={false}
            />
          ))
        : null}
      {mode === "caret" && showCaret ? (
        <KonvaRect
          x={layout.caret.x}
          y={layout.caret.y}
          width={Math.max(1, scale)}
          height={layout.caret.height}
          fill={block.fill}
          listening={false}
        />
      ) : null}
    </>
  );
}

let textMeasureCanvas: HTMLCanvasElement | null = null;

function createCanvasTextMeasurer(fontFamily: string, fontStyle: string, fontSize: number) {
  return (value: string) => {
    if (typeof document === "undefined") {
      return value.length * fontSize * 0.5;
    }

    textMeasureCanvas = textMeasureCanvas ?? document.createElement("canvas");
    const context = textMeasureCanvas.getContext("2d");

    if (!context) {
      return value.length * fontSize * 0.5;
    }

    const style = fontStyle.includes("italic") ? "italic" : "normal";
    const weight = fontStyle.includes("bold") ? "700" : "400";
    context.font = `${style} ${weight} ${fontSize}px ${toCssFontFamily(fontFamily)}`;

    return context.measureText(value).width;
  };
}

function toCssFontFamily(fontFamily: string): string {
  return JSON.stringify(fontFamily);
}
